import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Image,
  Icon,
  keyframes,
} from "@chakra-ui/react";
import {
  IoSparklesOutline,
  IoStorefrontOutline,
  IoMegaphoneOutline,
  IoTicketOutline,
  IoShieldCheckmarkOutline,
} from "react-icons/io5";

// Very subtle drift for the green corner glow on the Proximy Guarantee panel.
// The base gradient is intentionally still and slate-dark — only the orb
// breathes, so the effect reads as "alive" rather than "marketing banner".
const orbDrift = keyframes`
  0%   { transform: translate(0px, 0px)   scale(1);    opacity: 0.18; }
  50%  { transform: translate(-12px, 14px) scale(1.08); opacity: 0.24; }
  100% { transform: translate(0px, 0px)   scale(1);    opacity: 0.18; }
`;
const orbDriftAnimation = `${orbDrift} 12s ease-in-out infinite`;

// Editorial photography for each audience track. We pull from Pexels with
// `auto=compress&cs=tinysrgb&w=1200` so the modal stays light even on slow
// connections, and crop everything to a consistent aspect via the parent
// container's fixed height + objectFit="cover".
const PEXELS = (id) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=1200`;

const VENUES_IMAGE = PEXELS(17513729);
const PRODUCERS_IMAGE = PEXELS(3023317);
const GUESTS_IMAGE = PEXELS(9124759);

// Three audience tracks, mirrored visually from the hero.
const HOW_IT_WORKS_SECTIONS = [
  {
    eyebrow: "For Venues",
    eyebrowIcon: IoStorefrontOutline,
    accent: "#508cf7",
    accentSoft: "blue.50",
    accentBorder: "blue.100",
    headline: "Your space has a story waiting to be told.",
    image: VENUES_IMAGE,
    imagePosition: "center",
    steps: [
      {
        title: "List for Free",
        body: "Register your space in minutes. No fees, no fine print, no commitment.",
      },
      {
        title: "Get Discovered",
        body: "Passionate Event Producers will find your space and bring their vision and their audience — straight to your door.",
      },
      {
        title: "You Decide",
        body: "Every request comes to you first. Approve, decline, or keep browsing. Full control, always.",
      },
      {
        title: "Earn More, Give More",
        body: "Turn downtime into income, empty rooms into energy, and your space into a cornerstone of your community.",
      },
    ],
  },
  {
    eyebrow: "For Event Producers",
    eyebrowIcon: IoMegaphoneOutline,
    accent: "#a855f7",
    accentSoft: "purple.50",
    accentBorder: "purple.100",
    headline: "Design incredible experiences people will never forget.",
    image: PRODUCERS_IMAGE,
    imagePosition: "center",
    steps: [
      {
        title: "Join the Community",
        body: "Create a free account and apply to become a verified Community Event Producer.",
      },
      {
        title: "Find Your Stage",
        body: "Scout spaces that match your vision. Already have a spot in mind? Invite them to join Proximy.",
      },
      {
        title: "Build Something Real",
        body: "Use Proximy's tools to design, shape, and launch an experience from the ground up.",
      },
      {
        title: "Get the Green Light",
        body: "Submit to your chosen venue. When they say yes, you're ready to move.",
      },
      {
        title: "Make it Happen",
        body: "Promote your event, fill every seat, run the show, and get paid doing what you love.",
      },
    ],
  },
  {
    eyebrow: "For Guests",
    eyebrowIcon: IoTicketOutline,
    accent: "#ec4899",
    accentSoft: "pink.50",
    accentBorder: "pink.100",
    headline: "Show up. Connect. Feel something.",
    image: GUESTS_IMAGE,
    imagePosition: "center 35%",
    steps: [
      {
        title: "Discover Real Experiences",
        body: "Browse one-of-a-kind events hosted by real people, in your city — not corporations, not algorithms.",
      },
      {
        title: "Support Local",
        body: "Every event you attend puts money in the hands of a local creator who poured everything into it.",
      },
      {
        title: "Buy a Ticket, Do Some Good",
        body: "A portion of every ticket goes toward mental health support in your community.",
      },
    ],
  },
];

export const HowItWorksModal = ({ isOpen, onClose }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "5xl", lg: "6xl" }}
      isCentered
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.500" />
      <ModalContent
        borderRadius={{ base: 0, md: "2xl" }}
        overflow="hidden"
        my={{ base: 0, md: 8 }}
      >
        <ModalHeader
          pb={3}
          pt={5}
          px={{ base: 5, md: 7 }}
          borderBottom="1px solid"
          borderColor="gray.100"
          bg="white"
          position="sticky"
          top={0}
          zIndex={2}
        >
          <HStack spacing={3} align="center">
            <Box
              bg="blue.50"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={IoSparklesOutline} color="#508cf7" boxSize={4} />
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="700" color="gray.800">
                How Proximy works
              </Text>
              <Text fontSize="xs" color="gray.400">
                Built for venues, producers, and the people who show up.
              </Text>
            </Box>
          </HStack>
          <ModalCloseButton top={4} right={4} />
        </ModalHeader>

        <ModalBody px={{ base: 5, md: 7 }} py={{ base: 6, md: 8 }}>
          <VStack spacing={{ base: 12, md: 14 }} align="stretch">
            {HOW_IT_WORKS_SECTIONS.map((section, idx) => (
              <Flex
                key={section.eyebrow}
                direction={{
                  base: "column",
                  lg: idx % 2 === 0 ? "row" : "row-reverse",
                }}
                gap={{ base: 6, md: 8, lg: 10 }}
                align={{ base: "stretch", lg: "center" }}
              >
                {/* Visual */}
                <Box flex="1" w="full" position="relative">
                  <Box
                    position="absolute"
                    inset={0}
                    bg={section.accent}
                    opacity={0.18}
                    filter="blur(50px)"
                    borderRadius="32px"
                    transform="translateY(16px)"
                    zIndex={0}
                  />
                  <Box
                    position="relative"
                    zIndex={1}
                    borderRadius="20px"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="gray.100"
                    boxShadow="0 16px 36px rgba(15,23,42,0.12)"
                    bg="white"
                  >
                    <Image
                      src={section.image}
                      alt={section.eyebrow}
                      w="full"
                      h={{ base: "220px", md: "280px", lg: "320px" }}
                      objectFit="cover"
                      objectPosition={section.imagePosition || "center"}
                      loading="lazy"
                    />
                    {/* Subtle top-down vignette so the eyebrow chip stays
                        readable across any source image. */}
                    <Box
                      position="absolute"
                      inset={0}
                      bgGradient="linear(to-b, blackAlpha.300, transparent 35%, transparent 70%, blackAlpha.200)"
                      pointerEvents="none"
                    />
                    <HStack
                      position="absolute"
                      top={3}
                      left={3}
                      spacing={2}
                      bg="whiteAlpha.900"
                      backdropFilter="blur(10px)"
                      borderRadius="full"
                      px={3}
                      py={1.5}
                      boxShadow="0 4px 12px rgba(0,0,0,0.1)"
                    >
                      <Icon
                        as={section.eyebrowIcon}
                        boxSize="14px"
                        color={section.accent}
                      />
                      <Text
                        fontSize="11px"
                        fontWeight="700"
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                        color="gray.800"
                      >
                        {section.eyebrow}
                      </Text>
                    </HStack>
                  </Box>
                </Box>

                {/* Copy + numbered steps */}
                <Box flex="1.05" w="full">
                  <Text
                    fontSize="11px"
                    fontWeight="800"
                    letterSpacing="0.1em"
                    textTransform="uppercase"
                    color={section.accent}
                    mb={2}
                  >
                    {section.eyebrow}
                  </Text>
                  <Text
                    className="font-gilroy-bold"
                    fontSize={{ base: "22px", md: "26px" }}
                    lineHeight={{ base: "28px", md: "32px" }}
                    letterSpacing="-0.01em"
                    color="gray.800"
                    mb={5}
                  >
                    {section.headline}
                  </Text>

                  <VStack spacing={3.5} align="stretch">
                    {section.steps.map((step, sIdx) => (
                      <HStack
                        key={step.title}
                        spacing={3}
                        align="flex-start"
                      >
                        <Flex
                          minW="26px"
                          h="26px"
                          borderRadius="full"
                          bg={section.accentSoft}
                          border="1px solid"
                          borderColor={section.accentBorder}
                          color={section.accent}
                          align="center"
                          justify="center"
                          fontSize="11px"
                          fontWeight="800"
                          flexShrink={0}
                        >
                          {sIdx + 1}
                        </Flex>
                        <Box>
                          <Text
                            fontSize="14px"
                            fontWeight="700"
                            color="gray.800"
                            mb="2px"
                          >
                            {step.title}
                          </Text>
                          <Text
                            fontSize="13px"
                            color="gray.500"
                            lineHeight="1.6"
                          >
                            {step.body}
                          </Text>
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </Flex>
            ))}

            {/* ── Proximy Guarantee ─────────────────────────────────── */}
            <Box
              position="relative"
              borderRadius="24px"
              overflow="hidden"
              bgGradient="linear(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)"
              p={{ base: 6, md: 8 }}
              boxShadow="0 16px 40px rgba(15,23,42,0.22)"
            >
              {/* Green orb — gently drifts/breathes on a slow loop. */}
              <Box
                position="absolute"
                top="-80px"
                right="-80px"
                w="240px"
                h="240px"
                bg="#22c55e"
                filter="blur(70px)"
                borderRadius="full"
                pointerEvents="none"
                animation={orbDriftAnimation}
                sx={{
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                    opacity: 0.18,
                  },
                }}
              />
              <Box
                position="absolute"
                bottom="-90px"
                left="-90px"
                w="220px"
                h="220px"
                bg="#508cf7"
                opacity={0.15}
                filter="blur(70px)"
                borderRadius="full"
                pointerEvents="none"
              />

              <Box position="relative" zIndex={1}>
                <HStack
                  spacing={4}
                  align="flex-start"
                  mb={4}
                  flexWrap={{ base: "wrap", md: "nowrap" }}
                >
                  <Flex
                    w="44px"
                    h="44px"
                    minW="44px"
                    borderRadius="14px"
                    bg="whiteAlpha.100"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon
                      as={IoShieldCheckmarkOutline}
                      color="#22c55e"
                      boxSize="20px"
                    />
                  </Flex>
                  <VStack align="flex-start" spacing={1}>
                    <Text
                      fontSize="11px"
                      fontWeight="800"
                      letterSpacing="0.12em"
                      textTransform="uppercase"
                      color="whiteAlpha.700"
                    >
                      The Proximy Guarantee
                    </Text>
                    <Text
                      className="font-gilroy-bold"
                      fontSize={{ base: "20px", md: "26px" }}
                      lineHeight={{ base: "26px", md: "32px" }}
                      letterSpacing="-0.01em"
                      color="white"
                    >
                      We only run events that are ready to run.
                    </Text>
                  </VStack>
                </HStack>

                <VStack spacing={3} align="stretch" maxW="780px">
                  <Text
                    fontSize={{ base: "13px", md: "14px" }}
                    color="whiteAlpha.800"
                    lineHeight="1.7"
                  >
                    Every event on Proximy has to earn its place. If ticket
                    sales don't cover the venue before showtime, the event is
                    called off — automatically, 5 hours in advance.
                  </Text>
                  <Text
                    fontSize={{ base: "13px", md: "14px" }}
                    color="whiteAlpha.800"
                    lineHeight="1.7"
                  >
                    No awkward refunds at the door. No half-empty rooms. No one
                    losing money on a bad night. Just events built for success.
                  </Text>
                </VStack>
              </Box>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
